import {type PropsWithChildren, useRef} from 'react';

import useFocusTrap from '@/hooks/use-focus-trap/use-focus-trap';
import {StyledModalContainer} from './styles';

interface Props extends PropsWithChildren {
  onClose?: () => void;
}

/**
 * @description Focus management component to manage focus within a modal.
 * enables accessibility features like focus trapping and closing on ESC key press.
 * @param {Props} props - Props for the ModalFocusManagement component.
 * @param {Function} props.onClose - Function to call when the modal is requested to close (e.g., on ESC key press).
 * @param {React.ReactNode} props.children - The child components to be rendered within the focus management container.
 * @returns {JSX.Element} The ModalFocusManagement wrapper component.
 */
const ModalFocusManagement = ({children, onClose}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useFocusTrap({containerRef: ref, onEscKeydown: onClose, slideAnimationDuration: 300});

  return (
    <StyledModalContainer ref={ref} tabIndex={-1}>
      {children}
    </StyledModalContainer>
  );
};

export default ModalFocusManagement;
